// Service layer for the Card Editor feature. Each function maps 1:1 to a
// backend endpoint. axios's baseURL is set globally in main.js, so paths
// here are relative.

import axios from 'axios';

/**
 * @typedef {Object} CardRecord
 * @property {number} id
 * @property {'FACTOID'|'RECIPE'|'TIP'} type
 * @property {Array<TextBlock|ImageBlock>} blocks
 * @property {'DRAFT'|'PENDING'|'APPROVED'|'REJECTED'|'ARCHIVED'} status
 * @property {number} sort_order
 * @property {number|null} created_by
 * @property {string} created_at
 * @property {string} updated_at
 *
 * @typedef {Object} TextBlock
 * @property {'TITLE'|'BODY_1'|'BODY_2'|'BODY_3'} kind
 * @property {{en: string|null, de: string|null, fr: string|null, it: string|null}} text
 *
 * @typedef {Object} ImageBlock
 * @property {'IMAGE_1'|'IMAGE_2'} kind
 * @property {number|null} image_id
 */

/**
 * List all cards, ordered by sort_order ASC.
 * @returns {Promise<CardRecord[]>}
 */
export async function listCards() {
  const { data } = await axios.get('/cards/list');
  return data.cards;
}

/**
 * Fetch one card by id.
 * @param {number} id
 * @returns {Promise<CardRecord>}
 */
export async function fetchCard(id) {
  const { data } = await axios.get(`/cards/get/${id}`);
  return data;
}

/**
 * Upsert a card. If `payload.id` is present, updates; otherwise creates.
 * @param {Object} payload — { id?, type, blocks, status, sort_order? }
 * @returns {Promise<CardRecord>}
 */
export async function saveCard(payload) {
  const { data } = await axios.post('/cards/save', payload);
  return data;
}

/**
 * Hard-delete a card by id. Resolves on 204; rejects on any other status.
 * @param {number} id
 */
export async function deleteCard(id) {
  await axios.post(`/cards/delete/${id}`);
}

// --- Backwards-compatible aliases (the previous stub surface used different names) ---
export const createCard = saveCard;
export const updateCard = (id, payload) => saveCard({ ...payload, id });
